import { useState, useEffect, useRef } from "react";

/**
 * useInView — fires once when the element scrolls into view.
 * Falls back to visible=true when IntersectionObserver is unavailable (SSR/tests).
 */
export function useInView(threshold = 0.06) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

/** reveal — spreadable style props for a fade/slide-up entrance. */
export const reveal = (visible, delay = 0) => ({
  style: {
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(10px)",
    transition: "opacity 0.65s ease, transform 0.65s ease",
    transitionDelay: `${delay}ms`,
  },
});

/**
 * Reveal — wraps children in a scroll-reveal container.
 * Use `as` to change the element; extra props pass through.
 */
export function Reveal({
  children,
  delay = 0,
  threshold = 0.06,
  as: Tag = "div",
  className = "",
  ...rest
}) {
  const [ref, visible] = useInView(threshold);
  return (
    <Tag ref={ref} className={className} {...reveal(visible, delay)} {...rest}>
      {children}
    </Tag>
  );
}

export default Reveal;
